<template>
  <div class="results-grid js-results-grid">
    <div class="results-grid__items">
      <div
        v-for="result in results"
        :key="
          `${result.parentSlug ? `${result.parentSlug}-` : ''}${result.slug}${
            result.colorName ? `-${result.colorName}` : ''
          }`
        "
        class="results-grid__item-wrapper"
      >
        <a
          :href="
            linkURL({
              parentType: result.parentType,
              parentName: result.parentSlug,
              variantName: result.slug,
              colorName: result.colorName,
            })
          "
          @click="setReturnFilterAndSearchData(result)"
        >
          <img :src="imgSrc(result)" />
          <div class="results-grid__item-info">
            <div class="results-grid__item-parent-name">
              {{ result.parentName }}
            </div>
            <div class="results-grid__item-variant-name">
              {{ result.name }}
            </div>
          </div>
        </a>
      </div>
    </div>
    <NoResultsOrError
      v-if="results.length === 0 && !loadingData"
      :results="results"
      :apiCallMade="apiCallMade"
      :error="error"
      :no-results-message="noResultsMessage"
      :no-results-redirect="noResultsRedirect"
      :no-results-redirect-text="noResultsRedirectText"
      :no-results-open-chat="noResultsOpenChat"
    />
  </div>
</template>

<script>
import NoResultsOrError from '../shared/NoResultsOrError';
import { fireAdobeAnalyticsEvent } from '../../helpers/adobe-analytics';

const basePathsByType = {
  Interlayer: '/interlayers/:parentName/:variantName',
  GraphicPattern: '/graphic-patterns/:parentName/:variantName/:color',
  ColorType: '/color/:parentName',
  Color: '/color/:parentName',
};

export default {
  name: 'ResultsGrid',
  components: {
    NoResultsOrError,
  },
  props: [
    'results',
    'error',
    'apiCallMade',
    'currentPage',
    'totalResults',
    'perPage',
    'totalPages',
    'noResultsMessage',
    'noResultsRedirect',
    'noResultsRedirectText',
    'noResultsOpenChat',
    'loadingData',
    'selectedSampleType',
  ],
  data() {
    return {
      maxVariantsToDisplay: 5,
      noBackgroundColorTypes: ['ColorType', 'Interlayer'],
      noGradientTypes: ['ColorType'],
    };
  },
  methods: {
    displayVariants(variants) {
      return variants.slice(0, this.maxVariantsToDisplay);
    },
    extraVariantsCount(variants) {
      return variants.length - this.maxVariantsToDisplay;
    },
    linkURL(item) {
      const basePath = basePathsByType[item.parentType];

      return (
        basePath
          .replace(':parentName', item.parentName)
          .replace(':variantName', item.variantName)
          .replace(':color', item.colorName) +
        `?sample_type=${this.selectedSampleType || ''}`
      );
    },
    setReturnFilterAndSearchData(result) {
      this.sendAdobeSelectionEvent(result);

      const url = new URL(window.location.href);
      const filterOptions = url.searchParams.get('filterOptions');
      const searchTerm = url.searchParams.get('searchTerm');

      // these cookies are used by the View All Patterns CTA on the detail page
      this.setCookie('filterOptions', filterOptions);
      this.setCookie('searchTerm', searchTerm);
    },
    sendAdobeSelectionEvent(result) {
      const resultDisplayName = [result.parentName, result.name]
        .filter(Boolean)
        .filter(
          (value, index, self) =>
            self.findIndex(v => v.toLowerCase() === value.toLowerCase()) ===
            index
        )
        .join(' ')
        .replace(/\b\w/g, char => char.toUpperCase());

      // TODO: This could will result in Chroma and Varia colors having the same
      // string ie. "Jewel B11"... need to verify that this is okay.
      const eventData = {
        type: 'tool',
        detail: {
          eVar35: `Scroll Page|Select|${resultDisplayName}`,
        },
      };

      fireAdobeAnalyticsEvent(eventData);
    },
    setCookie(name, value, maxAgeSeconds = 3600) {
      if (value) {
        document.cookie = `${name}=${encodeURIComponent(
          value
        )}; path=/; max-age=${maxAgeSeconds}`;
      } else {
        // Clear the cookie by setting an expiration date in the past
        document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      }
    },
    resultClass(result) {
      let classes = 'results-grid__item';

      if (result.isLightText) classes += ' is-light-text';

      if (this.noGradientTypes.includes(result.parentType))
        classes += ' no-gradient';

      return classes;
    },
    imgSrc(resultOrVariant) {
      if (this.selectedSampleType === 'color-chip') {
        if (resultOrVariant.imgSrc?.includes('imageboss.me')) {
          return resultOrVariant.imgSrc.replace(':dimensions', 'cover/250x320');
        }
        return `https://img.imageboss.me/cover/250x320/${resultOrVariant.imgSrc}`;
      }
      if (resultOrVariant.imgSrc?.includes('imageboss.me')) {
        return resultOrVariant.imgSrc.replace(':dimensions', 'width/250');
      }
      return `https://img.imageboss.me/width/250/${resultOrVariant.imgSrc}`;
    },
  },
};
</script>
